'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdMedication,
    MdClose,
    MdWarning,
    MdFilterList,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getMedications,
    deleteMedication,
    Medication,
    MedicationFilters,
    MEDICATION_TYPE_LABELS,
    MEDICATION_CONTROL_TYPE_LABELS,
} from '@/lib/api/stock/medication-api'

export default function MedicationsPage() {
    const navigate = useNavigate()
    const [medications, setMedications] = useState<Medication[]>([])
    const [filteredMedications, setFilteredMedications] = useState<
        Medication[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [medicationToDelete, setMedicationToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [filters, setFilters] = useState<MedicationFilters>({})
    const [showFilters, setShowFilters] = useState(false)

    useEffect(() => {
        loadMedications()
    }, [filters])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = medications.filter(
                med =>
                    med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    med.genericName
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    med.manufacturer
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredMedications(filtered)
        } else {
            setFilteredMedications(medications)
        }
    }, [searchTerm, medications])

    const loadMedications = async () => {
        setLoading(true)
        try {
            const response = await getMedications(filters)
            if (response.success) {
                setMedications(response.result)
                setFilteredMedications(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar medicamentos')
            }
        } catch (error) {
            toast.error('Erro ao carregar medicamentos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setMedicationToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!medicationToDelete) return

        setDeleting(true)
        try {
            const response = await deleteMedication(medicationToDelete)
            if (response.success) {
                toast.success('Medicamento excluído com sucesso')
                loadMedications()
                setShowDeleteModal(false)
                setMedicationToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir medicamento')
            }
        } catch (error) {
            toast.error('Erro ao excluir medicamento')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando medicamentos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdMedication className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Medicamentos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {medications.length}{' '}
                            {medications.length === 1
                                ? 'medicamento'
                                : 'medicamentos'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/stock/medications/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Adicionar</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, genérico ou fabricante..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <button
                        className="btn btn-outline gap-2"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList className="text-lg" />
                        Filtros
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-base-300/50 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Tipo</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.type || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        type:
                                            (e.target.value as any) ||
                                            undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {Object.entries(MEDICATION_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Controle</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.controlType || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        controlType:
                                            (e.target.value as any) ||
                                            undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {Object.entries(
                                    MEDICATION_CONTROL_TYPE_LABELS
                                ).map(([key, label]) => (
                                    <option key={key} value={key}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">SNGPC</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={
                                    filters.requiresSngpc === undefined
                                        ? ''
                                        : filters.requiresSngpc
                                          ? 'true'
                                          : 'false'
                                }
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        requiresSngpc:
                                            e.target.value === ''
                                                ? undefined
                                                : e.target.value === 'true',
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                <option value="true">Controlado</option>
                                <option value="false">Nao Controlado</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {filteredMedications.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMedication className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum medicamento encontrado'
                                : 'Nenhum medicamento cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando medicamentos ao estoque'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() =>
                                    navigate('/stock/medications/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro medicamento
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Medicamento</th>
                                <th>Tipo</th>
                                <th>Controle</th>
                                <th>Estoque</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMedications.map(med => (
                                <tr
                                    key={med.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(`/stock/medications/${med.id}`)
                                    }
                                >
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdMedication className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <div className="font-bold">
                                                    {med.name}
                                                </div>
                                                {med.genericName && (
                                                    <div className="text-sm text-base-content/60">
                                                        {med.genericName}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {MEDICATION_TYPE_LABELS[med.type]}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${med.requiresSngpc ? 'badge-warning' : 'badge-ghost'}`}
                                        >
                                            {
                                                MEDICATION_CONTROL_TYPE_LABELS[
                                                    med.controlType
                                                ]
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            Min: {med.minimumStock}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            {med.isHighAlert && (
                                                <span
                                                    className="tooltip"
                                                    data-tip="Alto Alerta"
                                                >
                                                    <MdWarning className="text-warning text-lg" />
                                                </span>
                                            )}
                                            <span
                                                className={`badge ${med.isActive ? 'badge-success' : 'badge-ghost'}`}
                                            >
                                                {med.isActive
                                                    ? 'Ativo'
                                                    : 'Inativo'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/stock/medications/${med.id}/editar`
                                                    )
                                                }}
                                                className="btn btn-ghost btn-sm"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    handleDelete(med.id)
                                                }}
                                                className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setMedicationToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir medicamento"
                message="Tem certeza de que deseja excluir este medicamento? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
